import { useDashboard } from "../../context/DashboardContext";
import { useAuth } from "../../context/AuthContext";
import { timeAgo } from "../../lib/format";
import DateRangePicker from "./DateRangePicker";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { RefreshCw, LogOut, Settings, ChevronDown, Building2 } from "lucide-react";

export default function Topbar({ onOpenAdmin, isAdmin }) {
  const { branches, branchId, setBranchId, syncing, globalSync } = useDashboard();
  const { user, logout } = useAuth();
  const initials = (user?.name || user?.email || "A").slice(0, 2).toUpperCase();

  const selected = branchId === "all" ? null : branches.find((b) => b.id === branchId);
  const lastSync = selected
    ? selected.meta_last_synced
    : branches[0]?.meta_last_synced;

  return (
    <header className="sticky top-0 z-40 bg-[#0F172A]/90 backdrop-blur-md border-b border-white/10">
      <div className="px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 h-9 pl-2.5 pr-1 rounded-full bg-[#1E293B] border border-white/10">
            <Building2 className="w-4 h-4 text-[#38BDF8]" />
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="branch-switcher"
                className="h-8 border-0 bg-transparent text-slate-200 text-sm font-medium focus:ring-0 gap-1 min-w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="hidden md:flex items-center gap-1.5 text-xs text-slate-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
            </span>
            Synced {timeAgo(lastSync)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <DateRangePicker />
          <Button data-testid="global-sync-btn" onClick={globalSync} disabled={syncing}
            className="h-9 bg-[#1E293B] hover:bg-[#334155] border border-white/10 text-slate-200 rounded-full text-xs font-medium">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing" : "Sync"}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn"
                className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full bg-[#1E293B] border border-white/10 hover:bg-[#334155] transition-colors">
                <span className="w-7 h-7 rounded-full bg-[#38BDF8] text-[#0F172A] flex items-center justify-center text-xs font-bold">
                  {initials}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1E293B] border-white/10 text-slate-200 w-56">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 font-normal">{user?.email}</p>
                <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#38BDF8]/15 text-[#38BDF8]">
                  {user?.role}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {isAdmin && (
                <DropdownMenuItem data-testid="open-admin-btn" onClick={onOpenAdmin}
                  className="cursor-pointer focus:bg-[#334155]">
                  <Settings className="w-4 h-4 mr-2" /> Budget & Services
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-btn" onClick={logout}
                className="cursor-pointer focus:bg-[#334155] text-[#EF4444]">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
